import React, { PureComponent } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import classnames from 'classnames';
import { filter, get, map } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { Badge, Button, IconButton, ListItemText, MenuItem, MenuList } from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';

import { RolloutEnum } from '@lib/resources/types';

import InfiniteScroll from 'components/InfiniteScroll';
import MenuContainer from 'components/MenuContainer';
import { parseDateFromNow } from 'utils/helpers';
import { hasAccessToProduct } from 'utils/permissions';
import { selectPermissions } from 'selectors';

const styles = (theme) => {
  const {
    palette: {
      common: { white },
      text,
    },
    spacing: { unit },
  } = theme;

  return {
    badgeHidden: {
      height: 15,
      opacity: 0,
      right: 0,
      top: unit,
      width: 15,
    },
    badgeRoot: {
      padding: 0,
    },
    badgeVisible: {
      color: white,
      fontSize: 10,
      fontVariantNumeric: 'lining-nums',
      fontFeatureSettings: 'lnum',
      height: 15,
      opacity: 1,
      right: 0,
      top: unit,
      width: 15,
    },
    cursor: {
      cursor: 'pointer',
    },
    fade: {
      maxHeight: 384,
      minWidth: 608,
      overflowY: 'auto',
    },
    highlight: {},
    menuButton: {
      display: 'inline-block',
      margin: `${unit}px 0px`,
    },
    menuList: {
      padding: 0,
    },
    notificationActions: {
      width: '15%',
      display: 'inline-block',
      float: 'right',
      lineHeight: '20px',
    },
    notificationButton: {
      height: 26,
      lineHeight: '24px',
      marginBottom: 5,
      padding: 0,
      width: 88,
    },
    notificationButtonLabel: {
      color: text.primary,
      fontSize: 9,
      padding: 0,
      textTransform: 'uppercase',
    },
    notificationDate: {
      display: 'inline-block',
      width: '100%',
      fontSize: 9,
      color: 'gray',
    },
    notificationItem: {
      cursor: 'initial',
      fontWeight: 500,
      height: 'auto',
      padding: unit * 2,
      whiteSpace: 'normal',
    },
    notificationListItem: {
      padding: 0,
    },
    notificationMessage: {
      display: 'inline-flex',
      flex: 1,
      verticalAlign: 'middle',
      padding: 0,
      minHeight: 48,
    },
    notificationMessageText: {
      display: 'inline-block',
      lineHeight: '22px',
      width: '85%',
      padding: 0,
      fontSize: 12,
      float: 'left',
    },
    notificationText: {
      display: 'flex',
      flex: 1,
      fontWeight: 500,
      '&$highlight': {
        fontWeight: 600,
      },
    },
    zeroNotificationsContainer: {
      margin: '8px',
      display: 'flex',
      flex: 1,
      fontWeight: 500,
      justifyContent: 'center',
      fontSize: '18px',
    },
  };
};

class NotificationsCenter extends PureComponent {
  constructor(props) {
    super(props);

    this.renderHtmlMessage = this.renderHtmlMessage.bind(this);
    this.renderNotification = this.renderNotification.bind(this);

    this.state = {
      menuAnchor: null,
    };
  }

  handleButtonClick = (event) => {
    const { menuAnchor } = this.state;

    if (!event || menuAnchor) {
      this.setState({ menuAnchor: null });
    } else if (!menuAnchor) {
      this.setState({ menuAnchor: event.currentTarget });
    }
  };

  handleItemClick = (url) => {
    browserHistory.push(url);

    this.handleRequestClose();
  };

  handleReferralCalledAction = (event, referral, action) => {
    const { onReferralCalledMessageAction } = this.props;

    event.preventDefault();

    onReferralCalledMessageAction({ _id: referral, status: action === 'cancel' ? 'canceled' : 'scheduled' });
  };

  handleRequestClose = () => {
    this.setState({ menuAnchor: null });
  };

  handleRequestOpen = (event) => {
    const { menuAnchor } = this.state;

    if (!menuAnchor) this.setState({ menuAnchor: event.currentTarget });
  };

  handleViewChatAction(data) {
    const patientId = data.patient && typeof data.patient === 'object' ? data.patient._id : data.patient;
    browserHistory.push(`/patients/${patientId}/chat`);
    this.handleRequestClose();
  }

  renderProviderReferralCalledButtons(notification) {
    const { classes } = this.props;
    const { template, data } = notification;

    if (template === 'providerReferralCalled' && get(data, 'referral', false)) {
      return (
        <div>
          <Button
            classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
            onClick={(e) => this.handleReferralCalledAction(e, data.referral, 'schedule')}
            variant="text"
          >
            Schedule
          </Button>
          <Button
            classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
            onClick={(e) => this.handleReferralCalledAction(e, data.referral, 'cancel')}
            variant="text"
          >
            Cancel
          </Button>
        </div>
      );
    }
  }

  renderAppointmentButton(data) {
    const { classes } = this.props;

    if (get(data, 'appointment', false) && get(data, 'patient', false)) {
      return (
        <Button
          classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
          onClick={() => this.handleItemClick(`/patients/${data.patient}/appointments`)}
          variant="text"
        >
          View Appt
        </Button>
      );
    }
  }

  renderReferralButton(data) {
    const { classes } = this.props;

    if (get(data, 'referral', false)) {
      return (
        <Button
          classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
          onClick={() => this.handleItemClick(`/inbound-referrals/${data.referral}`)}
          variant="text"
        >
          View Referral
        </Button>
      );
    }
  }

  renderOfferButton(data) {
    const { classes } = this.props;

    if (get(data, 'offer', false)) {
      return (
        <Button
          classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
          onClick={() => this.handleItemClick(`/offers/${data.offer}`)}
          variant="text"
        >
          View Offer
        </Button>
      );
    }
  }

  renderChatButton(data, template) {
    const { classes } = this.props;
    const isHubTemplate = /internalChat|providerNewInboundChatActivity/.test(template);
    if (get(data, 'patient', false) && isHubTemplate) {
      return (
        <Button
          classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
          onClick={() => this.handleViewChatAction(data)}
          variant="text"
        >
          View Chat
        </Button>
      );
    }
  }

  renderBroadcastButton(data) {
    const { classes, permissions } = this.props;

    if (get(data, 'broadcast', false)) {
      return (
        <Button
          classes={{ root: classes.notificationButton, label: classes.notificationButtonLabel }}
          onClick={() =>
            this.handleItemClick(
              `${hasAccessToProduct(permissions, RolloutEnum.OldBroadcasts) ? '/v1' : ''}/broadcasts/${data.broadcast}`,
            )
          }
          variant="text"
        >
          View Broadcast
        </Button>
      );
    }
  }

  renderMessageContent(notification) {
    const { classes, permissions } = this.props;
    const { data, message, updatedAt } = notification;
    const messageTextReplaced = message.replace(/<a[^>]*>/gi, '').replace(/<\/a>/gi, '');

    if (get(data, 'patient')) {
      return (
        <div
          onClick={() => this.handleViewChatAction(data)}
          className={classnames(classes.notificationMessageText, classes.cursor)}
        >
          {messageTextReplaced}
          <span className={classes.notificationDate}>{parseDateFromNow(updatedAt)}</span>
        </div>
      );
    }

    if (get(data, 'broadcast', false)) {
      return (
        <div
          className={classnames(classes.notificationMessageText, classes.cursor)}
          onClick={() =>
            this.handleItemClick(
              `${hasAccessToProduct(permissions, RolloutEnum.OldBroadcasts) ? '/v1' : ''}/broadcasts/${data.broadcast}`,
            )
          }
        >
          {messageTextReplaced}
          <span className={classes.notificationDate}>{parseDateFromNow(updatedAt)}</span>
        </div>
      );
    }

    return (
      <div className={classes.notificationMessageText}>
        {messageTextReplaced}
        <span className={classes.notificationDate}>{parseDateFromNow(updatedAt)}</span>
      </div>
    );
  }

  renderHtmlMessage(notification) {
    const { classes } = this.props;
    const { data, template } = notification;

    return (
      <div className={classes.notificationMessage}>
        {this.renderMessageContent(notification)}
        <div className={classes.notificationActions}>
          {this.renderOfferButton(data)}
          {this.renderReferralButton(data)}
          {this.renderAppointmentButton(data)}
          {this.renderChatButton(data, template)}
          {this.renderBroadcastButton(data)}
          {this.renderProviderReferralCalledButtons(notification)}
        </div>
      </div>
    );
  }

  renderNotification(notification) {
    const { classes } = this.props;

    return (
      <MenuItem className={classes.notificationItem} key={notification._id}>
        <ListItemText
          classes={{
            root: classes.notificationListItem,
            primary: classnames(classes.notificationText, { [classes.highlight]: !notification.read }),
          }}
          primary={this.renderHtmlMessage(notification)}
        />
      </MenuItem>
    );
  }

  render() {
    const {
      classes,
      hasMore,
      onLoadMore,
      onNotificationsCenterIconClick,
      notifications,
      iconColor,
      renderCustomIcon,
    } = this.props;
    const { menuAnchor } = this.state;
    const unreadMessages = get(
      filter(notifications, (n) => !n.read),
      'length',
      0,
    );
    const badgeClass = unreadMessages > 0 ? classes.badgeVisible : classes.badgeHidden;

    return (
      <Badge
        badgeContent={unreadMessages}
        classes={{
          root: classes.menuButton,
          badge: badgeClass,
          colorSecondary: classes.badgeRoot,
        }}
        color="secondary"
        onClick={onNotificationsCenterIconClick}
      >
        {renderCustomIcon ? (
          renderCustomIcon(this.handleRequestOpen)
        ) : (
          <IconButton
            style={iconColor ? { color: iconColor } : {}}
            aria-label="Notifications"
            color="primary"
            onClick={this.handleRequestOpen}
          >
            <NotificationsIcon fontSize="small" />
          </IconButton>
        )}
        <MenuContainer
          className={classes.popper}
          classNameFade={classes.fade}
          menuAnchor={menuAnchor}
          onClickAway={this.handleButtonClick}
          placement="bottom"
        >
          <InfiniteScroll hasMore={hasMore} initialLoad={false} loadMore={onLoadMore} useWindow={false}>
            <MenuList className={classes.menuList}>
              {notifications.length !== 0 ? (
                map(notifications, this.renderNotification)
              ) : (
                <MenuItem className={classes.zeroNotificationsContainer}>No notifications yet.</MenuItem>
              )}
            </MenuList>
          </InfiniteScroll>
        </MenuContainer>
      </Badge>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  permissions: selectPermissions,
});

const withConnect = connect(mapStateToProps);

NotificationsCenter.propTypes = {
  classes: PropTypes.object.isRequired,
  hasMore: PropTypes.bool.isRequired,
  notifications: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  onLoadMore: PropTypes.func.isRequired,
  onNotificationsCenterIconClick: PropTypes.func.isRequired,
  onReferralCalledMessageAction: PropTypes.func.isRequired,
  iconColor: PropTypes.string,
  renderCustomIcon: PropTypes.func,
};

export default compose(withConnect, withStyles(styles))(NotificationsCenter);
